"use client";

import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Pagination, Skeleton } from "antd";
import { urlFor } from "@/sanity/lib/image";
import { getPostsByAuthor } from "@/sanity/actions/blogActions";
import { PortableText } from "@portabletext/react";
import { PortableTextComponents } from "@/components/blog/PortableTextComponents";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";
import Image from "next/image";

import HeroSection from "@/components/common/HeroSection";
import BlogCard from "@/components/blog/BlogCard";
import { BlogCardSkeleton } from "@/components/skeletons/BlogCardSkeleton";

export default function AuthorPageClient({ authorSlug }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const isdark = useIsDark();

  const [posts, setPosts] = useState([]);
  const [author, setAuthor] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [postsLoading, setPostsLoading] = useState(true);
  const [authorLoading, setAuthorLoading] = useState(true);

  const currentPage = Number(searchParams.get("page")) || 1;
  const cardsPerPage = 3;

  const getAvatarUrl = (image, size = 48, quality = 80) => {
    try {
      if (!image) return "/assets/fallbacks/avatar-fallback.svg";
      return urlFor(image)
        .width(size)
        .height(size)
        .quality(quality)
        .auto("format")
        .url();
    } catch (e) {
      return "/assets/fallbacks/avatar-fallback.svg";
    }
  };

  useEffect(() => {
    async function loadAuthorPosts() {
      setPostsLoading(true);
      setAuthorLoading(true);
      const { posts, total, author } = await getPostsByAuthor(
        authorSlug,
        currentPage,
        cardsPerPage
      );
      setPosts(posts);
      setTotalItems(total);
      setAuthor(author);
      setPostsLoading(false);
      setAuthorLoading(false);
    }
    loadAuthorPosts();
  }, [authorSlug, currentPage]);

  if (!author && !authorLoading && !postsLoading) {
    return (
      <div className="text-center py-20 text-gray-400">Author not found</div>
    );
  }

  return (
    <div className="relative flex flex-col justify-center items-center mt-6 mx-5 md:mx-[40px] xl:mx-[100px] 3xl:mx-[150] z-10">
      <HeroSection
        heading="AUTHOR"
        subHeading={author?.name || "Loading..."}
        paragraph={`Explore all blog posts by ${author?.name || "this author"}.`}
        headingStyles="text-[45px] mobile:text-[77px] sm:text-[100px] md:text-[110px] tablet:text-[130px] lg:text-[160px] xl:text-[190px] laptop:text-[228px] 3xl:text-[260px] tracking-[0.01em] leading-[100%] text-center opacity-100 "
        paragraphStyles="xl:w-[962px] xl:text-[32px]"
      />

      {/* Author Info */}
      <div
        className={twMerge(
          "relative flex flex-col items-center justify-center w-full max-w-4xl mx-auto p-6 md:p-8 lg:p-10 rounded-[20px] lg:rounded-[40px] mt-16 mb-10",
          isdark ? "bg-[#0F0F0F]" : "bg-[#F2E4FE]"
        )}
      >
        {author ? (
          <>
            {/* Author Avatar */}
            <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full overflow-hidden relative mb-6">
              <Image
                src={
                  getAvatarUrl(author.image, 160, 90)
                }
                alt={author.name}
                fill
                sizes="160px"
                quality={90}
                className="object-cover"
              />
            </div>

            {/* Author Name */}
            <h1 className="font-alexandria font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground text-center mb-4">
              {author.name}
            </h1>

            {/* Author Bio */}
            {author.bio && (
              <div
                className={twMerge(
                  "prose prose-invert max-w-none text-center",
                  isdark ? "text-[#909090]" : "text-[#5A5A6E]"
                )}
              >
                <PortableText
                  value={author.bio}
                  components={PortableTextComponents}
                />
              </div>
            )}

            {/* Post Count */}
            <p
              className={twMerge(
                "font-manrope font-medium text-lg mt-4",
                isdark ? "text-[#909090]" : "text-[#5A5A6E]"
              )}
            >
              {totalItems} {totalItems === 1 ? "post" : "posts"} published
            </p>
          </>
        ) : (
          <>
            <Skeleton.Avatar
              active
              size={160}
              className="mb-6"
              style={{
                backgroundColor: isdark ? "#374151" : "",
              }}
            />

            {/* Author Name Skeleton */}
            <Skeleton.Input
              active
              size="large"
              className="mb-4 !w-64 !h-12"
              style={{
                backgroundColor: isdark ? "#374151" : "",
              }}
            />

            {/* Author Bio Skeleton */}
            <div className="flex flex-col items-center gap-2 mb-4 w-full max-w-lg">
              <Skeleton
                active
                paragraph={{ rows: 3, width: ["100%", "90%", "80%"] }}
                title={false}
                style={{
                  "--ant-skeleton-color": isdark ? "#374151" : "",
                  "--ant-skeleton-to-color": isdark ? "#4B5563" : "",
                }}
              />
            </div>

            {/* Post Count Skeleton */}
            <Skeleton.Input
              active
              size="small"
              className="!w-32 !h-6"
              style={{
                backgroundColor: isdark ? "#374151" : "",
              }}
            />
          </>
        )}
      </div>

      {/* Grid of cards */}
      <div className="mt-16 w-full">
        <div className="grid grid-cols-1 mobile:grid-cols-2 md:grid-cols-3 tablet:grid-cols-3 gap-4 mobile:gap-3 sm:gap-4 md:gap-3 tablet:gap-6 lg:gap-10 2xl:gap-20 3xl:gap-30">
          {postsLoading ? (
            <>
              {/* Cards Skeleton */}
              {Array.from({ length: 6 }).map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </>
          ) : (
            posts.map((card) => (
              <BlogCard
                key={card._id}
                image={urlFor(card.mainImage).width(370).height(180).url()}
                blurDataURL={card.mainImage?.asset?.metadata?.lqip}
                author={card.authorName}
                authorSlug={card.authorSlug}
                authorImage={getAvatarUrl(card.authorImage, 48, 80)}
                date={new Date(card.publishedAt).toDateString()}
                title={card.title}
                description={card.excerpt || ""}
                slug={card.slug}
              />
            ))
          )}
        </div>

        {posts.length === 0 && !postsLoading && (
          <div className="text-center py-20 text-gray-400">
            No posts found by this author.
          </div>
        )}
      </div>

      {/* Pagination */}
      {/* {totalItems > cardsPerPage && (
          <div className="flex justify-center mt-10 mb-10 px-2">
            <Pagination
              current={currentPage}
              pageSize={cardsPerPage}
              total={totalItems}
              onChange={(page) => router.push(`?page=${page}`)}
              showSizeChanger={false}
              className="custom-pagination"
            />
          </div>
        )} */}

      <div className="flex justify-center mt-10 mb-10 px-2">
        <Pagination
          current={currentPage}
          pageSize={cardsPerPage}
          total={totalItems}
          onChange={(page) => router.push(`?page=${page}`)}
          showSizeChanger={false}
          className="custom-pagination"
        />
      </div>
    </div>
  );
}
